/** Gemeinsame Typen für App und Worker. Eine Quelle, damit beide Seiten nicht
 *  auseinanderlaufen — der häufigste Fehler bei getrennten Frontend/Backend-Paketen. */

export type Owner = "sarah" | "mario";
export type Status = "read" | "reading" | "want" | "dnf";
export type TagKind = "genre" | "thema" | "stimmung" | "epoche" | "frei";

export const OWNERS: Owner[] = ["sarah", "mario"];
export const OWNER_NAME: Record<Owner, string> = {
  sarah: "Sarah",
  mario: "Mario",
};
export const other = (o: Owner): Owner => (o === "sarah" ? "mario" : "sarah");

/** Anzeigetexte für den Status. "abgebrochen" ist ausdrücklich neutral formuliert —
 *  es ist ein Signal für Empfehlungen, keine schlechte Note. */
export const STATUS_LABEL: Record<Status, string> = {
  read: "gelesen",
  reading: "lese ich gerade",
  want: "will ich lesen",
  dnf: "abgebrochen",
};

export interface Tag {
  id: number;
  kind: TagKind;
  name: string;
}

export interface Book {
  id: number;
  owner: Owner;
  isbn: string | null;
  title: string;
  author: string;
  link_key: string;

  series: string | null;
  series_no: number | null;
  series_lock: 0 | 1;

  status: Status;
  rating: number | null;
  stars: number | null;
  stars_seen: 0 | 1;
  fav: 0 | 1;

  read_year: string | null;
  read_date: string | null;
  pages: number | null;
  pub_year: string | null;
  publisher: string | null;
  language: string | null;

  description: string | null;
  desc_source: string | null;
  notes: string | null;

  cover: 0 | 1;
  cover_key: string | null;
  cover_ratio: number | null;
  cover_avg: string | null;
  cover_edge: string | null;
  cover_dark: 0 | 1;

  source: string;
  source_id: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;

  /** angereichert von der API, nicht in der Tabelle: */
  tags?: Tag[];
  lists?: number[];
  /** steht dasselbe Werk auch im anderen Regal? Reine Anzeige, nichts wird zusammengeführt. */
  also?: boolean;
}

export interface BookList {
  id: number;
  owner: Owner;
  name: string;
  kind: "system" | "custom";
  sort: number;
  created_at: string;
  deleted_at: string | null;
  count?: number;
}

export type RecoMode =
  | "katalog"
  | "tags"
  | "mixer"
  | "nebenan"
  | "duett"
  | "blinddate";

export const RECO_LABEL: Record<RecoMode, string> = {
  katalog: "Aus dem ganzen Regal",
  tags: "Nach einzelnen Tags",
  mixer: "Aus dem Wölkchen-Mixer",
  nebenan: "Aus dem Regal nebenan",
  duett: "Für euch beide",
  blinddate: "Blind Date",
};

export interface Recommendation {
  id: number;
  owner: Owner;
  mode: RecoMode;
  title: string;
  author: string;
  isbn: string | null;
  why: string | null;
  anchors: { id?: number; title: string }[];
  tags: string[];
  teaser: string | null;
  /** 'lokal' = Buch aus einem der beiden Regale · 'vorrat' = einmalig
   *  vorbereiteter Vorschlag von außerhalb (Tabelle `reco_pool`) · 'ki' = frisch
   *  erzeugt. Die drei dürfen in der Anzeige nicht vermischt werden — ein
   *  Vorrats-Treffer als "Vorschlag der KI" auszuweisen wäre schlicht falsch. */
  source: "lokal" | "vorrat" | "ki";
  book_id: number | null;
  score: number | null;
  created_at: string;
  saved_at: string | null;
  hidden_at: string | null;
}

export interface SeriesInfo {
  name: string;
  books: Book[];
  have: number[];
  gaps: number[];
  max: number | null;
}

export interface Stats {
  owner: Owner;
  total: number;
  byStatus: Record<Status, number>;
  byYear: { year: string; count: number }[];
  noYear: number;
  authors: { author: string; count: number }[];
  rated: number;
  avgRating: number | null;
  favs: number;
  pages: number;
  longestSeries: { name: string; count: number } | null;
  tags: { name: string; kind: TagKind; count: number }[];
  withCover: number;
  withDescription: number;
  tagged: number;
}

/** Antwortform aller schreibenden Endpunkte — eindeutige Zustände statt "irgendwas kam zurück". */
export interface WriteResult<T = unknown> {
  ok: boolean;
  status: "ok" | "warnung" | "fehler";
  message?: string;
  data?: T;
}
